import { FC, useEffect, useState } from "react";
import { useAppSelector } from "../../../app/store/store";
import { calendarService, ILessonHistoryEvent, TLessonStatus } from "../../../entities/calendar";
import { useGlobalMessageActions } from "../../../entities/globalMessage";
import { useMyActions } from "../../../entities/my";
import { MyError } from "../../../shared/lib/error/MyError";
import { AuthError } from "../../../shared/lib/error/AuthError";
import { Modal } from "../../../shared/ui/modal";
import { LoaderSpinner } from "../../../shared/ui/spinner";
import { formatMoscowDateTime } from "../../../shared/lib/helpers/moscowDateTime";
import classes from './calendarLessonHistory.module.scss'

interface IProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    lessonId: string;
}

const STATUS_LABELS: Record<TLessonStatus, string> = {
    LESSON_STATUS_UNSPECIFIED: 'Неизвестно',
    LESSON_STATUS_SCHEDULED: 'Запланировано',
    LESSON_STATUS_COMPLETED: 'Проведено',
    LESSON_STATUS_CANCELLED: 'Отменено',
    LESSON_STATUS_RESCHEDULED: 'Перенесено',
}

const formatDateTime = (value?: string): string => {
    if(!value) return '—'

    const date = new Date(value)
    if(Number.isNaN(date.getTime())) return '—'

    return formatMoscowDateTime(date, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

const isCreatedByReschedule = (event: ILessonHistoryEvent): boolean => {
    return event.fromStatus === 'LESSON_STATUS_UNSPECIFIED'
        && event.toStatus === 'LESSON_STATUS_SCHEDULED'
        && Boolean(event.previousStartsAt && event.newStartsAt)
}

const getEventTitle = (event: ILessonHistoryEvent): string => {
    if(isCreatedByReschedule(event)) return 'Занятие создано при переносе'
    if(event.toStatus === 'LESSON_STATUS_COMPLETED') return 'Занятие проведено'
    if(event.toStatus === 'LESSON_STATUS_CANCELLED') return 'Занятие отменено'
    if(event.toStatus === 'LESSON_STATUS_RESCHEDULED') return 'Занятие перенесено'
    if(event.toStatus === 'LESSON_STATUS_SCHEDULED') return 'Занятие запланировано'

    return 'Изменение занятия'
}

export const CalendarLessonHistory: FC<IProps> = ({open, setOpen, lessonId}) => {
    const {my} = useAppSelector(state => state.myReducer)

    const [events, setEvents] = useState<ILessonHistoryEvent[]>([])
    const [isLoading, setIsLoading] = useState<boolean>(false)

    const {setIsAuth} = useMyActions()
    const {setGlobalMessage} = useGlobalMessageActions()

    useEffect(() => {
        if(!open) return

        let isActual = true

        const getData = async () => {
            try{
                setIsLoading(true)
                setEvents([])

                const response = await calendarService.getLessonHistory(lessonId)

                if(isActual) setEvents(response.events ?? [])
            }
            catch(error){
                if(!isActual) return

                console.log(error)
                if(error instanceof AuthError){
                    setIsAuth(false)
                    setGlobalMessage({message: error.message, type: 'error'})
                }
                else if(error instanceof MyError){
                    setGlobalMessage({message: error.message, type: 'error'})
                }
                else{
                    setGlobalMessage({message: 'Ошибка при получении истории занятия', type: 'error'})
                }
            }
            finally{
                if(isActual) setIsLoading(false)
            }
        }

        getData()

        return () => {
            isActual = false
        }
    }, [lessonId, open, setGlobalMessage, setIsAuth])

    return (
        <Modal open={open} setOpen={setOpen}>
            <section className={classes.container}>
                <section className={classes.header}>
                    <section>
                        <h3>История занятия</h3>
                        <p>Статусы, переносы и комментарии по уроку</p>
                    </section>
                    <button type="button" className={classes.closeButton} onClick={() => setOpen(false)}>×</button>
                </section>

                {isLoading && (
                    <section className={classes.loader}>
                        <LoaderSpinner />
                    </section>
                )}

                {!isLoading && !events.length && (
                    <section className={classes.empty}>
                        История по этому занятию пока пустая
                    </section>
                )}

                {!isLoading && Boolean(events.length) && (
                    <ul className={classes.timeline}>
                        {events.map(event => (
                            <li key={event.id} className={classes.event}>
                                <section className={classes.eventMarker} />
                                <section className={classes.eventContent}>
                                    <section className={classes.eventTop}>
                                        <h4>{getEventTitle(event)}</h4>
                                        <span>{formatDateTime(event.audit?.createdAt)}</span>
                                    </section>

                                    {!isCreatedByReschedule(event) && (
                                        <section className={classes.statusLine}>
                                            <span>{STATUS_LABELS[event.fromStatus] ?? '—'}</span>
                                            <span className={classes.arrow}>→</span>
                                            <span>{STATUS_LABELS[event.toStatus] ?? '—'}</span>
                                        </section>
                                    )}

                                    {(event.previousStartsAt || event.newStartsAt) && (
                                        <section className={classes.timeChange}>
                                            <span>Время:</span>
                                            <b>{formatDateTime(event.previousStartsAt)}</b>
                                            <span>→</span>
                                            <b>{formatDateTime(event.newStartsAt)}</b>
                                        </section>
                                    )}

                                    {!(my.role === 'tutor' && event.toStatus === 'LESSON_STATUS_CANCELLED') && event.comment && (
                                        <section className={classes.comment}>
                                            {event.comment}
                                        </section>
                                    )}

                                    {!(my.role === 'tutor' && event.toStatus === 'LESSON_STATUS_CANCELLED') && event.audit?.createdByUserId && (
                                        <section className={classes.audit}>
                                            Автор изменения: {
                                                event.audit.createdByFullName
                                                    ? `${event.audit.createdByFullName}${event.audit.createdByRoleName ? ` · ${event.audit.createdByRoleName}` : ''}`
                                                    : `#${event.audit.createdByUserId}`
                                            }
                                        </section>
                                    )}
                                </section>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </Modal>
    )
}
